import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  CircularProgress,
  CardContent,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import {
  Assessment,
  People,
  Business,
  Today,
  Download,
} from '@mui/icons-material';
import { useSuperadminAuditTrailReport } from '../../hooks/useSuperAdmin';
import { colors } from '../../theme/theme';
import { format } from 'date-fns';
import { DateFilter, DateFilterValue } from '../../components/common';

export const AuditTrailPage = () => {
  const [page, setPage] = useState(0);
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('all');
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  const { report, isLoading, error } = useSuperadminAuditTrailReport(page + 1, {
    date_from: startDate,
    date_to: endDate,
  });

  const handleDateFilterChange = (filter: DateFilterValue, start?: string, end?: string) => {
    setDateFilter(filter);
    setStartDate(start);
    setEndDate(end);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error">Failed to load audit trail report</Alert>
      </AdminLayout>
    );
  }

  const auditColumns: GridColDef[] = [
    {
      field: 'created_at',
      headerName: 'Date & Time',
      flex: 1.2,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? format(params.value, 'MMM dd, yyyy HH:mm') : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: 'User',
      flex: 1.2,
      valueGetter: (value, row) => row.user ? `${row.user.first_name} ${row.user.last_name}` : 'N/A',
    },
    {
      field: 'county',
      headerName: 'County',
      flex: 1,
      valueGetter: (value, row) => row.county?.name || 'N/A',
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.5,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: colors.primary.main,
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            System Audit Trail
          </Typography>
          <Box display="flex" gap={2}>
            <DateFilter
              value={dateFilter}
              onChange={handleDateFilterChange}
              startDate={startDate}
              endDate={endDate}
            />
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                },
              }}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3} mb={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total Actions
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.totalActions || 0}
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Unique Users
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.uniqueUsers || 0}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Active Counties
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.uniqueCounties || 0}
                    </Typography>
                  </Box>
                  <Business sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Today's Actions
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.todayActions || 0}
                    </Typography>
                  </Box>
                  <Today sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Recent Activity
            </Typography>
            {report?.auditTrails?.data && report.auditTrails.data.length > 0 ? (
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={report.auditTrails.data}
                  columns={auditColumns}
                  rowCount={report.auditTrails.total || 0}
                  paginationMode="server"
                  paginationModel={{ page, pageSize: 15 }}
                  onPaginationModelChange={(model) => setPage(model.page)}
                  pageSizeOptions={[15]}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No audit trail data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
